import { useEffect, useState } from "react";
import SidebarManager from "../components/SidebarManager";
import Header from "../components/Header";
import Table from "../components/Table";
import EditCompanyModal from "../components/EditCompanyModal";
import {
  getCompanyDetails,
  updateCompanyDetail,
} from "../services/companyService";
import { getIndustry } from "../services/industryService";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function UnassignedCompanies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [industries, setIndustries] = useState([]);
  const { user } = useAuth();

  // ✅ Fetch companies
  const fetchCompanies = async () => {
    try {
      const response = await getCompanyDetails(user);
      const data = response.filter((x) => x.applicationStatus.id === 1);
      setCompanies(data);
    } catch (error) {
      console.error("Failed to load company data:", error);
    }
  };

  // ✅ Fetch industries
  const fetchIndustries = async () => {
    try {
      const res = await getIndustry();
      const data = res;
      const items = Array.isArray(data) ? data : data.data;
      setIndustries(items || []);
    } catch (err) {
      console.error("Industry fetch error", err);
    }
  };

  // ✅ Run both on load
  useEffect(() => {
    fetchCompanies();
    fetchIndustries();
  }, []);

  // ✅ Update company via PUT
  const handleUpdateCompany = async (updatedData) => {
    if (!editingCompany) return;

    const payload = {
      companyId: editingCompany.id,
      version: editingCompany.version || 1,
      websiteUrl: updatedData.websiteUrl,
      structureId: 0,
      numberOfEmployees: 0,
      yearOfEstablishment: 0,
      telephoneFax: [],
      products: [],
      services: [],
      // extra custom fields you manage client-side, the following fields are not ready in the api yet so this form wound't work
      name: updatedData.name,
      registeredName: updatedData.registeredName,
      contactNumber: updatedData.contactNumber,
      emailAddress: updatedData.emailAddress,
      industryIds: [parseInt(updatedData.industryId)],
    };

    try {
      const response = await updateCompanyDetail(payload);

      if (!response.status) throw new Error("Failed to update company");

      await fetchCompanies();
      setShowEditModal(false);
    } catch (err) {
      console.error("Update failed:", err);
      alert("Could not update the company.");
    }
  };

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const columns = [
    { label: "Company Name", key: "name" },
    { label: "Address", key: "registeredName", align: "center" },
    { label: "Email", key: "websiteUrl", align: "center" },
    {
      label: "Industry",
      key: "industries",
      align: "center",
      format: "arrayValue",
    },
    {
      label: "Action",
      key: "find",
      align: "center",
      render: (row) => (
        <button
          className="bg-[#6f81f5] text-white px-4 py-1 rounded-lg text-sm hover:opacity-90 cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            navigate(`/assign-forms?companyId=${row.id}`);
          }}
        >
          Assign
        </button>
      ),
    },
    {
      label: "Edit",
      key: "edit",
      align: "center",
      render: (row) => (
        <div className="flex gap-2 justify-center">
          <button
            title="Edit"
            onClick={() => {
              setEditingCompany(row);
              setShowEditModal(true);
            }}
            className=" cursor-pointer"
          >
            <img
              src="/icons/edit.svg"
              alt="Edit"
              className="w-7 h-7"
              draggable="false"
            />
          </button>
          <button title="Delete" className=" cursor-pointer">
            <img
              src="/icons/delete.svg"
              alt="Delete"
              className="w-7 h-7"
              draggable="false"
            />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="py-8 px-[5rem] bg-[#fbfbfb] w-screen mx-auto">
      <Header title="Company Profile" date={today} />

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">Unassigned Companies</h2>
          <p className="text-sm text-gray-500">
            Total: {companies.length} Numbers
          </p>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center gap-2 border px-4 py-2 text-sm rounded-md text-gray-600">
            <img src="/icons/excel.svg" alt="Download" className="w-4 h-4" />
            Download Excel
          </button>
          <button className="flex items-center gap-2 border px-4 py-2 text-sm rounded-md text-gray-600">
            <img src="/icons/sort.svg" alt="Sort" className="w-4 h-4" />
            Sort data
          </button>
        </div>
      </div>

      <Table columns={columns} rows={companies} colour="#c5cdfb" />

      <EditCompanyModal
        company={editingCompany}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleUpdateCompany}
        industries={industries}
      />
    </div>
  );
}

export default UnassignedCompanies;
